import { useTranslation } from 'next-i18next'
import { NextSeo } from 'next-seo'
import { makeStaticProps } from '../lib/getStatic'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import ContactService from '../services/contact.service'
import { useNotification } from '../providers/NotificationProvider'
import { HeroIcons } from '../lib/utils/heroIcons'
import useLocale from '../hooks/useLocale'
import gtmService from '../services/gtm.service'

interface ContactFormData {
  firstName: string
  lastName: string
  email: string
  subject: 'issue' | 'question' | 'other'
  message: string
}

interface SocialLink {
  name: string
  href?: string
  icon: string
}

const socialLinks: SocialLink[] = [
  {
    name: 'LinkedIn',
    href: process.env.NEXT_PUBLIC_LINKEDIN_PAGE,
    icon: '/img/logos/brands/linkedin.svg',
  },
  {
    name: 'X',
    href: process.env.NEXT_PUBLIC_TWITTER_PAGE,
    icon: '/img/logos/brands/twitter.svg',
  },
  {
    name: 'Instagram',
    href: process.env.NEXT_PUBLIC_INSTAGRAM_PAGE,
    icon: '/img/logos/brands/instagram.svg',
  },
  {
    name: 'Discord',
    href: process.env.NEXT_PUBLIC_DISCORD_INVITE,
    icon: '/img/logos/brands/discord.svg',
  },
]

const inputClassName =
  'mt-2 h-11 w-full rounded-button border border-[#d9deed] bg-[#f8f9fd] px-4 text-shelters-body text-[#202942] placeholder:text-[#8790aa] transition focus:border-[#786ee2] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#786ee2]/10'

const textAreaClassName =
  'mt-2 w-full resize-none rounded-button border border-[#d9deed] bg-[#f8f9fd] px-4 py-3 text-shelters-body text-[#202942] placeholder:text-[#8790aa] transition focus:border-[#786ee2] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#786ee2]/10'

const timeSlots = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00']

const getDateKey = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')

export default function ContactPage() {
  const { t } = useTranslation('contact')
  const { locale } = useLocale()
  const { success: displaySuccess, error: displayError } = useNotification()
  const [sendingMessage, setSendingMessage] = useState(false)
  const [todayKey, setTodayKey] = useState('')
  const [visibleMonth, setVisibleMonth] = useState<Date | null>(null)
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')

  const messageTopics = [
    { id: 'issue', title: t('form.subject.input.issue'), value: 'issue' },
    {
      id: 'question',
      title: t('form.subject.input.question'),
      value: 'question',
    },
    { id: 'other', title: t('form.subject.input.other'), value: 'other' },
  ] as const

  const {
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    defaultValues: {
      subject: 'issue',
    },
  })

  const selectedSubject = watch('subject')
  const bookingHref = process.env.NEXT_PUBLIC_CALENDLY || '#'
  const weekdayLabelsResult = t('booking.weekdays', {
    returnObjects: true,
  })
  const weekdayLabels = Array.isArray(weekdayLabelsResult)
    ? (weekdayLabelsResult as string[])
    : []
  const calendarDays = visibleMonth
    ? (() => {
        const firstDayOffset =
          (new Date(
            visibleMonth.getFullYear(),
            visibleMonth.getMonth(),
            1,
          ).getDay() +
            6) %
          7
        const daysInMonth = new Date(
          visibleMonth.getFullYear(),
          visibleMonth.getMonth() + 1,
          0,
        ).getDate()
        const cellCount = firstDayOffset + daysInMonth <= 35 ? 35 : 42

        return Array.from({ length: cellCount }, (_, index) => {
          const date = new Date(
            visibleMonth.getFullYear(),
            visibleMonth.getMonth(),
            index - firstDayOffset + 1,
          )
          const dateKey = getDateKey(date)

          return {
            dateKey,
            day: date.getDate(),
            isCurrentMonth: date.getMonth() === visibleMonth.getMonth(),
            isPast: Boolean(todayKey && dateKey < todayKey),
            isWeekend: date.getDay() === 0 || date.getDay() === 6,
          }
        })
      })()
    : []
  const monthLabel = visibleMonth
    ? new Intl.DateTimeFormat(locale, {
        month: 'long',
        year: 'numeric',
      }).format(visibleMonth)
    : ''
  const currentMonthKey = todayKey.slice(0, 7)
  const visibleMonthKey = visibleMonth
    ? getDateKey(visibleMonth).slice(0, 7)
    : ''
  const canGoToPreviousMonth =
    Boolean(currentMonthKey) && visibleMonthKey > currentMonthKey
  const canConfirmBooking = Boolean(selectedDate && selectedTime)

  useEffect(() => {
    const today = new Date()
    setTodayKey(getDateKey(today))
    setVisibleMonth(new Date(today.getFullYear(), today.getMonth(), 1))
  }, [])

  const changeMonth = (offset: number) => {
    setSelectedDate('')
    setVisibleMonth((currentMonth) => {
      if (!currentMonth) return currentMonth

      return new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + offset,
        1,
      )
    })
  }

  const confirmBooking = () => {
    if (!canConfirmBooking || bookingHref === '#') return
    gtmService.scheduleMeetingIntent({
      ctaLocation: 'contact_booking_widget',
      destinationUrl: bookingHref,
      selectedDate,
      selectedTime,
    })
    gtmService.generateLead({
      leadType: 'meeting',
      leadSource: 'contact_booking_widget',
      ctaLocation: 'contact_booking_widget',
    })
    window.location.assign(bookingHref)
  }

  const onSubmitForm = async (data: ContactFormData) => {
    try {
      setSendingMessage(true)
      await ContactService.sendContactMail(
        data.firstName,
        data.lastName,
        data.email,
        data.subject,
        data.message,
      )
      gtmService.generateLead({
        leadType: 'contact_form',
        leadSource: 'contact_page',
        ctaLocation: 'contact_form',
        emailDomain: data.email.split('@')[1],
        subject: data.subject,
      })
      reset({ subject: 'issue' })
      displaySuccess(
        t('form.notifications.success.title'),
        t('form.notifications.success.subtitle'),
        10000,
      )
    } catch (err) {
      displayError(
        t('form.notifications.failure.title'),
        t('form.notifications.failure.subtitle'),
        10000,
      )
    } finally {
      setSendingMessage(false)
    }
  }

  return (
    <>
      <NextSeo
        title={t('seo.title') || ''}
        description={t('seo.description') || ''}
      />

      <main className="bg-gradient-box isolate min-h-screen overflow-hidden">
        <div className="pointer-events-none absolute -left-24 top-44 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.14)_0%,transparent_72%)]" />
        <div className="pointer-events-none absolute -right-24 top-24 h-96 w-96 rounded-full bg-[radial-gradient(circle,rgba(205,193,255,0.2)_0%,transparent_75%)]" />

        <section className="relative z-10 px-3 pb-24 pt-28 tablet:px-10 tablet:pt-32 desktop:px-24 2xl:px-44">
          <div className="mx-auto w-full">
            <div className="mb-8 max-w-3xl px-3 tablet:px-0">
              <h1 className="font-semibold text-white text-shelters-section-title">
                {t('title')}
              </h1>
              <p className="mt-3 max-w-2xl text-white/75 text-shelters-section-body">
                {t('subtitle')}
              </p>
            </div>

            <div className="grid gap-5 xl:grid-cols-[390px_1fr]">
              <aside className="order-2 flex h-full flex-col rounded-card border border-white/30 bg-[linear-gradient(145deg,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0.07)_100%)] p-5 text-white shadow-[0_14px_36px_rgba(18,15,62,0.22)] backdrop-blur-xl backdrop-saturate-150 xl:order-1">
                <h2 className="text-xl font-semibold text-white">
                  {t('booking.title')}
                </h2>
                <div className="mt-4 border-t border-white/20 pt-4">
                  <p className="text-sm font-medium text-white/85">
                    {t('booking.selectDate')}
                  </p>

                  <div className="mt-3 rounded-inner-card border border-white/20 bg-white/5 p-3 backdrop-blur-md">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => changeMonth(-1)}
                        disabled={!canGoToPreviousMonth}
                        aria-label={t('booking.previousMonth')}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-25"
                      >
                        <HeroIcons.ChevronRightIcon className="h-5 w-5 rotate-180" />
                      </button>
                      <p className="text-sm font-semibold capitalize text-white">
                        {monthLabel || '\u00A0'}
                      </p>
                      <button
                        type="button"
                        onClick={() => changeMonth(1)}
                        aria-label={t('booking.nextMonth')}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white transition hover:bg-white/10"
                      >
                        <HeroIcons.ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="mt-2 grid grid-cols-7 gap-1 text-center">
                      {weekdayLabels.map((weekday) => (
                        <span
                          key={weekday}
                          className="py-1 text-[0.72rem] font-medium text-white/60"
                        >
                          {weekday}
                        </span>
                      ))}
                      {calendarDays.map((calendarDay) => {
                        const isSelected = selectedDate === calendarDay.dateKey
                        const isDisabled =
                          !calendarDay.isCurrentMonth ||
                          calendarDay.isPast ||
                          calendarDay.isWeekend

                        return (
                          <button
                            key={calendarDay.dateKey}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => setSelectedDate(calendarDay.dateKey)}
                            aria-pressed={isSelected}
                            className={`mx-auto inline-flex h-8 w-8 items-center justify-center rounded-full text-sm transition ${
                              isSelected
                                ? 'bg-white text-[#4435b7] shadow-[0_6px_16px_rgba(18,15,62,0.22)]'
                                : isDisabled
                                  ? 'cursor-default text-white/25'
                                  : 'text-white/90 hover:bg-white/15'
                            }`}
                          >
                            {calendarDay.day}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <p className="mt-4 text-sm font-medium text-white/85">
                    {t('booking.selectTime')}
                  </p>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {timeSlots.map((timeSlot) => (
                      <button
                        key={timeSlot}
                        type="button"
                        onClick={() => setSelectedTime(timeSlot)}
                        aria-pressed={selectedTime === timeSlot}
                        className={`h-9 rounded-button border text-sm font-medium transition ${
                          selectedTime === timeSlot
                            ? 'border-white bg-white text-[#4435b7] shadow-[0_8px_18px_rgba(18,15,62,0.18)]'
                            : 'border-white/25 bg-white/5 text-white/85 hover:border-white/45 hover:bg-white/10'
                        }`}
                      >
                        {timeSlot}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    disabled={!canConfirmBooking || bookingHref === '#'}
                    onClick={confirmBooking}
                    className="mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-button border border-white/55 bg-white/10 px-4 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(18,15,62,0.16)] transition hover:-translate-y-0.5 hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0"
                  >
                    <HeroIcons.CalendarDaysIcon className="h-5 w-5" />
                    {t('booking.confirm')}
                  </button>
                  <p className="mt-3 text-center text-xs leading-5 text-white/55">
                    {t('booking.redirectNotice')}
                  </p>
                </div>

                <div className="mt-6 border-t border-white/20 pt-5">
                  <p className="text-shelters-kicker text-white/80">
                    {t('help.socialLabel')}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    {socialLinks.map((social) => (
                      <a
                        key={social.name}
                        href={social.href || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          social.href &&
                          gtmService.outboundClick({
                            linkUrl: social.href,
                            linkLabel: social.name,
                            linkLocation: 'contact_social_links',
                          })
                        }
                        aria-label={social.name}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-button border border-white/20 bg-white/10 transition hover:-translate-y-0.5 hover:bg-white/20"
                      >
                        <img
                          src={social.icon}
                          alt={social.name}
                          className="h-4 w-4 object-contain opacity-90"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </aside>

              <form
                className="order-1 rounded-card border border-white/80 bg-white/95 p-5 shadow-[0_18px_46px_rgba(18,15,62,0.18)] backdrop-blur-xl md:p-7 xl:order-2"
                onSubmit={handleSubmit(onSubmitForm)}
              >
                <h2 className="mb-6 text-xl font-semibold text-[#202942]">
                  {t('form.title')}
                </h2>

                <div className="grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                  <div>
                    <label className="text-shelters-kicker text-[#4f5a89]">
                      {t('form.information.input.firstName')}
                    </label>
                    <input
                      type="text"
                      placeholder="Jane"
                      className={inputClassName}
                      {...register('firstName', {
                        required: true,
                        maxLength: 64,
                      })}
                    />
                    {errors.firstName?.type === 'required' && (
                      <p className="mt-1 text-[0.8rem] text-[#c52c5a]">
                        {t('form.information.input.firstName')}{' '}
                        {t('form.errors.required')}
                      </p>
                    )}
                    {errors.firstName?.type === 'maxLength' && (
                      <p className="mt-1 text-[0.8rem] text-[#c52c5a]">
                        {t('form.information.input.firstName')}{' '}
                        {t('form.errors.max64')}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-shelters-kicker text-[#4f5a89]">
                      {t('form.information.input.lastName')}
                    </label>
                    <input
                      type="text"
                      placeholder="Smith"
                      className={inputClassName}
                      {...register('lastName', {
                        required: true,
                        maxLength: 64,
                      })}
                    />
                    {errors.lastName?.type === 'required' && (
                      <p className="mt-1 text-[0.8rem] text-[#c52c5a]">
                        {t('form.information.input.lastName')}{' '}
                        {t('form.errors.required')}
                      </p>
                    )}
                    {errors.lastName?.type === 'maxLength' && (
                      <p className="mt-1 text-[0.8rem] text-[#c52c5a]">
                        {t('form.information.input.lastName')}{' '}
                        {t('form.errors.max64')}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-shelters-kicker text-[#4f5a89]">
                      {t('form.information.input.email')}
                    </label>
                    <input
                      type="email"
                      placeholder="jsmith@email.com"
                      className={inputClassName}
                      {...register('email', {
                        required: true,
                        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      })}
                    />
                    {errors.email?.type === 'required' && (
                      <p className="mt-1 text-[0.8rem] text-[#c52c5a]">
                        {t('form.information.input.email')}{' '}
                        {t('form.errors.required')}
                      </p>
                    )}
                    {errors.email?.type === 'pattern' && (
                      <p className="mt-1 text-[0.8rem] text-[#c52c5a]">
                        {t('form.information.input.email')}{' '}
                        {t('form.errors.valid')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center gap-2">
                    <HeroIcons.ChatBubbleLeftRightIcon className="h-[18px] w-[18px] text-[#5d67ad]" />
                    <p className="text-shelters-kicker text-[#4f5a89]">
                      {t('form.subject.title')}
                    </p>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
                    {messageTopics.map((topic) => (
                      <label
                        key={topic.id}
                        className={`flex cursor-pointer items-center gap-2 rounded-button border px-3 py-2 transition ${
                          selectedSubject === topic.value
                            ? 'border-[#8d6dca] bg-[linear-gradient(135deg,rgba(141,109,202,0.16)_0%,rgba(90,93,231,0.12)_100%)]'
                            : 'border-[#d8def1] bg-white/60 hover:border-[#bfc7eb] hover:bg-white/85'
                        }`}
                      >
                        <input
                          type="radio"
                          value={topic.value}
                          className="h-4 w-4 border-[#9ea9dd] text-[#5a5de7] focus:ring-[#5a5de7]/25"
                          {...register('subject')}
                        />
                        <span className="text-[0.92rem] text-[#2b3458]">
                          {topic.title}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <label className="text-shelters-kicker text-[#4f5a89]">
                    {t('form.message.title')}
                  </label>
                  <p className="mt-1 text-[0.92rem] text-[#5f688f]">
                    {t('form.message.subtitle')}
                  </p>
                  <textarea
                    rows={4}
                    placeholder={
                      t('form.message.input.placeholder') || 'Message'
                    }
                    className={textAreaClassName}
                    {...register('message', {
                      required: true,
                      maxLength: 2000,
                    })}
                  />
                  {errors.message?.type === 'required' && (
                    <p className="mt-1 text-[0.8rem] text-[#c52c5a]">
                      {t('form.message.title')} {t('form.errors.required')}
                    </p>
                  )}
                  {errors.message?.type === 'maxLength' && (
                    <p className="mt-1 text-[0.8rem] text-[#c52c5a]">
                      {t('form.message.title')} {t('form.errors.max2000')}
                    </p>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="submit"
                    disabled={sendingMessage}
                    className="inline-flex h-11 items-center justify-center rounded-button bg-gradient px-5 text-[0.95rem] font-medium text-white shadow-[0_10px_24px_rgba(51,40,154,0.28)] transition hover:-translate-y-0.5 hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {t('form.send')}
                    <HeroIcons.PaperAirplaneIcon className="ml-2 h-4 w-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

const getStaticProps = makeStaticProps(['common', 'contact', 'index', 'navbar'])

export { getStaticProps, ContactPage }
